import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import LegalAppLink from "../../components/LegalAppLink";
import { usePagesTranslation } from "../../locales";
import DeletionStatusService from "./DeletionStatusService";
import { shouldUseLegalAppShell } from "../../../config/appWebViewLegal";
import DataDeletionFormLayout from "./DataDeletionFormLayout";

// Confirmation code: hex string (typically 16–32 chars from backend UUID prefix)
const VALID_CONFIRMATION_CODE = /^[0-9a-fA-F]{16,64}$/;

function DeletionStatusMain() {
    const { t } = usePagesTranslation();
    const location = useLocation();
    const { code: pathCode } = useParams();
    const [searchParams] = useSearchParams();
    const queryCode = searchParams.get("code");
    const urlCode = (pathCode || queryCode || "").trim();
    const hideAppChrome = shouldUseLegalAppShell(
        location.pathname,
        location.search,
        location.hash,
    );

    const [confirmationCode, setConfirmationCode] = useState(urlCode);
    const [loading, setLoading] = useState(false);
    const isValidCode = VALID_CONFIRMATION_CODE.test(confirmationCode.trim());
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (urlCode) {
            setConfirmationCode(urlCode);
        }
    }, [urlCode]);

    const handleCheck = async () => {
        const code = confirmationCode.trim();
        if (!code) {
            setError(t("core.legal.deletionStatus.enterCode"));
            setResult(null);
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const data = await DeletionStatusService.getDataDeletionStatus(code);
            if (data?.notFound) {
                setResult({ notFound: true });
            } else {
                setResult(data);
            }
        } catch (err) {
            setError(t("core.legal.deletionStatus.error"));
            setResult(null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <DataDeletionFormLayout>
            {!hideAppChrome ? (
                <LegalAppLink to="/legal#privacy" sx={{ display: "inline-block", mb: 2 }}>
                    {t("core.legal.deletionStatus.backToPrivacy")}
                </LegalAppLink>
            ) : null}
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                {t("core.legal.deletionStatus.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t("core.legal.deletionStatus.description")}
            </Typography>

            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", justifyContent: "flex-end", mb: 3 }}>
                <TextField
                    label={t("core.legal.deletionStatus.confirmationCode")}
                    value={confirmationCode}
                    onChange={(e) => setConfirmationCode(e.target.value)}
                    placeholder={t("core.legal.deletionStatus.confirmationCodePlaceholder")}
                    sx={{ flex: 1, minWidth: 0 }}
                    variant="outlined"
                    size="small"
                    onKeyDown={(e) => e.key === "Enter" && isValidCode && handleCheck()}
                    helperText={confirmationCode.trim() && !isValidCode ? t("core.legal.deletionStatus.codeFormat") : null}
                    error={!!(confirmationCode.trim() && !isValidCode)}
                />
                <Button
                    variant="contained"
                    onClick={handleCheck}
                    disabled={loading || !isValidCode}
                    sx={{
                        flexShrink: 0,
                        backgroundColor: "#3BA666",
                        "&:hover": {
                            backgroundColor: "#2E8B57",
                        },
                        "&.Mui-disabled": {
                            backgroundColor: "#ccc",
                            color: "#666",
                        },
                    }}
                >
                    {loading ? t("core.legal.deletionStatus.sending") : t("core.legal.deletionStatus.send")}
                </Button>
            </Box>

            {error && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            {result && (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 3,
                        backgroundColor: result.notFound ? "#fff8e1" : "#e8f5e9",
                        borderColor: result.notFound ? "#ffc107" : "#4caf50",
                    }}
                >
                    {result.notFound ? (
                        <Typography variant="body1">
                            {t("core.legal.deletionStatus.noData")}
                        </Typography>
                    ) : (
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                {t("core.legal.deletionStatus.resultTitle")}
                            </Typography>
                            {result.message && (
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {result.message}
                                </Typography>
                            )}
                            <Box component="dl" sx={{ m: 0, "& dd": { ml: 2, mb: 0.5 } }}>
                                <Typography component="dt" variant="body2" color="text.secondary">
                                    {t("core.legal.deletionStatus.confirmationCode")}
                                </Typography>
                                <Typography component="dd" variant="body2">{result.confirmation_code || "—"}</Typography>

                                <Typography component="dt" variant="body2" color="text.secondary">
                                    {t("core.legal.deletionStatus.channel")}
                                </Typography>
                                <Typography component="dd" variant="body2">{result.channel || "—"}</Typography>

                                <Typography component="dt" variant="body2" color="text.secondary">
                                    {t("core.legal.deletionStatus.status")}
                                </Typography>
                                <Typography component="dd" variant="body2">{result.status || "—"}</Typography>

                                <Typography component="dt" variant="body2" color="text.secondary">
                                    {t("core.legal.deletionStatus.requestedAt")}
                                </Typography>
                                <Typography component="dd" variant="body2">{result.requested_at || "—"}</Typography>

                                <Typography component="dt" variant="body2" color="text.secondary">
                                    {t("core.legal.deletionStatus.updatedAt")}
                                </Typography>
                                <Typography component="dd" variant="body2">{result.updated_at || "—"}</Typography>
                            </Box>
                        </Box>
                    )}
                </Paper>
            )}
        </DataDeletionFormLayout>
    );
}

export default DeletionStatusMain;
